/**
 * Rastreamento first-party do funil da plataforma Fastblog.
 * Captura visitante anônimo, UTMs (first e last touch) e passos do funil.
 * Nunca lança — analytics não pode quebrar o app.
 */
import { supabase } from "@/integrations/supabase/client";

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/growth-funnel-event-collect`;
const VISITOR_KEY = "fastblog_growth_visitor";
const TOUCH_KEY = "fastblog_growth_touch";
const CONSENT_KEY = "fastblog_cookie_consent";
const NINETY_DAYS = 90 * 24 * 60 * 60 * 1000;

export type GrowthStep =
  | "site_visit"
  | "cta_trial_click"
  | "signup_start"
  | "signup_complete"
  | "article_first"
  | "article_third"
  | "purchase";

type Touch = {
  first_utm_source?: string | null;
  first_utm_medium?: string | null;
  first_utm_campaign?: string | null;
  first_utm_term?: string | null;
  first_utm_content?: string | null;
  last_utm_source?: string | null;
  last_utm_medium?: string | null;
  last_utm_campaign?: string | null;
  last_utm_term?: string | null;
  last_utm_content?: string | null;
  last_referrer?: string | null;
  last_landing_page?: string | null;
  gclid?: string | null;
  fbclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  msclkid?: string | null;
  ttclid?: string | null;
  li_fat_id?: string | null;
  utm_id?: string | null;
  click_source?: string | null;
  campaign_id?: string | null;
  referrer?: string | null;
  landing_page?: string | null;
  savedAt?: number;
  user_id?: string | null;
  company_id?: string | null;
  lead_email?: string | null;
  lead_name?: string | null;
  lead_phone?: string | null;
};

function analyticsAllowed(): boolean {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return true; // sem decisão ainda: só medimos de forma anônima
    const parsed = JSON.parse(raw);
    return parsed?.analytics !== false;
  } catch {
    return true;
  }
}

function randomId(): string {
  try {
    return crypto.randomUUID().replace(/-/g, "");
  } catch {
    return `v${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
  }
}

/** Cookie first-party: sobrevive onde o localStorage é bloqueado/particionado
 *  (navegador in-app do Instagram/Facebook, ITP, modo restrito). */
function readCookie(name: string): string | null {
  try {
    const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(name: string, value: string, days = 365) {
  try {
    const exp = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    const domain = location.hostname.endsWith("fastblog.com.br") ? "; domain=.fastblog.com.br" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${exp}; path=/; SameSite=Lax${domain}`;
  } catch {
    /* ignore */
  }
}

export function getVisitorId(): string {
  let id: string | null = null;
  try {
    id = localStorage.getItem(VISITOR_KEY);
  } catch {
    /* ignore */
  }
  if (!id) id = readCookie(VISITOR_KEY);
  if (!id) id = randomId();
  try {
    localStorage.setItem(VISITOR_KEY, id);
  } catch {
    /* ignore */
  }
  writeCookie(VISITOR_KEY, id);
  return id;
}

function readTouch(): Touch {
  try {
    const raw = localStorage.getItem(TOUCH_KEY) ?? readCookie(TOUCH_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Touch;
    if (parsed.savedAt && Date.now() - parsed.savedAt > NINETY_DAYS) return {};
    return parsed;
  } catch {
    return {};
  }
}

function writeTouch(touch: Touch) {
  const payload = JSON.stringify({ ...touch, savedAt: touch.savedAt ?? Date.now() });
  try {
    localStorage.setItem(TOUCH_KEY, payload);
  } catch {
    /* ignore */
  }
  // Cookie tem limite de ~4KB; o touch é pequeno, mas protegemos mesmo assim.
  if (payload.length < 3500) writeCookie(TOUCH_KEY, payload);
}

function currentParams() {
  const p = new URLSearchParams(window.location.search);
  const gclid = p.get("gclid");
  const gbraid = p.get("gbraid");
  const wbraid = p.get("wbraid");
  const fbclid = p.get("fbclid");
  const msclkid = p.get("msclkid");
  const ttclid = p.get("ttclid");
  const li_fat_id = p.get("li_fat_id");
  const gadSource = p.get("gad_source");
  const gadCampaign = p.get("gad_campaignid");

  // Google Ads com auto-tagging não envia utm_*: derivamos a origem do clique.
  let click_source: string | null = null;
  if (gclid || gbraid || wbraid || gadSource || gadCampaign) click_source = "google_ads";
  else if (fbclid) click_source = "meta_ads";
  else if (msclkid) click_source = "bing_ads";
  else if (ttclid) click_source = "tiktok_ads";
  else if (li_fat_id) click_source = "linkedin_ads";

  return {
    utm_source: p.get("utm_source"),
    utm_medium: p.get("utm_medium"),
    utm_campaign: p.get("utm_campaign"),
    utm_term: p.get("utm_term"),
    utm_content: p.get("utm_content"),
    gclid,
    fbclid,
    gbraid,
    wbraid,
    msclkid,
    ttclid,
    li_fat_id,
    utm_id: p.get("utm_id"),
    click_source,
    campaign_id: gadCampaign || p.get("utm_id"),
  };
}

function deviceInfo() {
  const ua = navigator.userAgent;
  const isTablet = /iPad|Tablet/i.test(ua);
  const isMobile = !isTablet && /Mobi|Android|iPhone/i.test(ua);
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\//.test(ua)
      ? "Opera"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : /Firefox\//.test(ua)
            ? "Firefox"
            : "Outro";
  const os = /Windows/.test(ua)
    ? "Windows"
    : /Mac OS/.test(ua)
      ? "macOS"
      : /Android/.test(ua)
        ? "Android"
        : /iPhone|iPad|iOS/.test(ua)
          ? "iOS"
          : /Linux/.test(ua)
            ? "Linux"
            : "Outro";
  return {
    device_type: isTablet ? "tablet" : isMobile ? "mobile" : "desktop",
    browser,
    os,
    language: navigator.language || null,
    screen: typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : null,
  };
}

/** Guarda first touch na primeira visita e persiste o last touch entre páginas. */
function ensureTouch(): Touch {
  const stored = readTouch();
  const now = currentParams();
  const hasNewCampaign = !!(
    now.utm_source ||
    now.gclid ||
    now.fbclid ||
    now.gbraid ||
    now.wbraid ||
    now.msclkid ||
    now.ttclid ||
    now.li_fat_id ||
    now.utm_id ||
    now.click_source
  );
  const referrerNow = document.referrer || null;
  const pageNow = window.location.pathname + window.location.search;

  if (!stored.savedAt) {
    const fresh: Touch = {
      first_utm_source: now.utm_source,
      first_utm_medium: now.utm_medium,
      first_utm_campaign: now.utm_campaign,
      first_utm_term: now.utm_term,
      first_utm_content: now.utm_content,
      last_utm_source: now.utm_source,
      last_utm_medium: now.utm_medium,
      last_utm_campaign: now.utm_campaign,
      last_utm_term: now.utm_term,
      last_utm_content: now.utm_content,
      last_referrer: referrerNow,
      last_landing_page: pageNow,
      gclid: now.gclid,
      fbclid: now.fbclid,
      gbraid: now.gbraid,
      wbraid: now.wbraid,
      msclkid: now.msclkid,
      ttclid: now.ttclid,
      li_fat_id: now.li_fat_id,
      utm_id: now.utm_id,
      click_source: now.click_source,
      campaign_id: now.campaign_id,
      referrer: referrerNow,
      landing_page: pageNow,
      savedAt: Date.now(),
    };
    writeTouch(fresh);
    return fresh;
  }

  // Last touch só é sobrescrito quando há nova campanha — nunca com nulo.
  const next: Touch = { ...stored, last_landing_page: pageNow };
  if (hasNewCampaign) {
    next.last_utm_source = now.utm_source ?? stored.last_utm_source ?? null;
    next.last_utm_medium = now.utm_medium ?? stored.last_utm_medium ?? null;
    next.last_utm_campaign = now.utm_campaign ?? stored.last_utm_campaign ?? null;
    next.last_utm_term = now.utm_term ?? stored.last_utm_term ?? null;
    next.last_utm_content = now.utm_content ?? stored.last_utm_content ?? null;
    next.last_referrer = referrerNow ?? stored.last_referrer ?? null;
    next.gclid = now.gclid ?? stored.gclid ?? null;
    next.fbclid = now.fbclid ?? stored.fbclid ?? null;
    next.gbraid = now.gbraid ?? stored.gbraid ?? null;
    next.wbraid = now.wbraid ?? stored.wbraid ?? null;
    next.msclkid = now.msclkid ?? stored.msclkid ?? null;
    next.ttclid = now.ttclid ?? stored.ttclid ?? null;
    next.li_fat_id = now.li_fat_id ?? stored.li_fat_id ?? null;
    next.utm_id = now.utm_id ?? stored.utm_id ?? null;
    next.click_source = now.click_source ?? stored.click_source ?? null;
    next.campaign_id = now.campaign_id ?? stored.campaign_id ?? null;
  }
  writeTouch(next);
  return next;
}

function sessionPayload() {
  const touch = ensureTouch();
  return {
    ...touch,
    ...deviceInfo(),
    utm_source: touch.last_utm_source ?? touch.first_utm_source ?? null,
    utm_medium: touch.last_utm_medium ?? touch.first_utm_medium ?? null,
    utm_campaign: touch.last_utm_campaign ?? touch.first_utm_campaign ?? null,
    utm_term: touch.last_utm_term ?? touch.first_utm_term ?? null,
    utm_content: touch.last_utm_content ?? touch.first_utm_content ?? null,
    referrer: touch.referrer ?? (document.referrer || null),
    landing_page: touch.landing_page ?? window.location.pathname,
  };
}

function post(body: unknown): Promise<void> {
  const session = (body as { session?: { user_id?: string; company_id?: string } })?.session;
  if (session?.user_id || session?.company_id) {
    return supabase.auth.getSession().then(({ data }) => {
      if (!data.session) return;
      return fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${data.session.access_token}` },
        body: JSON.stringify(body), keepalive: true,
      }).then(() => undefined);
    }).catch(() => undefined);
  }
  const payload = JSON.stringify(body);
  try {
    if (navigator.sendBeacon) {
      // text/plain evita o preflight CORS (o beacon falhava com application/json).
      const queued = navigator.sendBeacon(
        ENDPOINT,
        new Blob([payload], { type: "text/plain;charset=UTF-8" }),
      );
      if (queued) return Promise.resolve();
    }
  } catch {
    /* ignore */
  }
  try {
    return fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    })
      .then(() => undefined)
      .catch(() => undefined);
  } catch {
    return Promise.resolve();
  }
}

export function trackGrowthStep(step: GrowthStep, metadata: Record<string, unknown> = {}): Promise<void> {
  if (typeof window === "undefined" || !analyticsAllowed()) return Promise.resolve();
  try {
    return post({
      visitor_id: getVisitorId(),
      session: sessionPayload(),
      events: [
        {
          step,
          occurred_at: new Date().toISOString(),
          page_url: window.location.pathname + window.location.search,
          metadata,
        },
      ],
    });
  } catch {
    return Promise.resolve();
  }
}

/** Anexa a campanha atual a uma URL interna (ex.: CTA -> /auth?tab=signup),
 *  para que a origem chegue ao cadastro mesmo se o storage do navegador falhar. */
export function withTrackingParams(url: string): string {
  try {
    const touch = readTouch();
    const target = new URL(url, window.location.origin);
    if (target.origin !== window.location.origin) return url;
    const map: Record<string, unknown> = {
      utm_source: touch.last_utm_source ?? touch.first_utm_source,
      utm_medium: touch.last_utm_medium ?? touch.first_utm_medium,
      utm_campaign: touch.last_utm_campaign ?? touch.first_utm_campaign,
      utm_term: touch.last_utm_term ?? touch.first_utm_term,
      utm_content: touch.last_utm_content ?? touch.first_utm_content,
      gclid: touch.gclid,
      fbclid: touch.fbclid,
      gbraid: touch.gbraid,
      wbraid: touch.wbraid,
      msclkid: touch.msclkid,
      ttclid: touch.ttclid,
      li_fat_id: touch.li_fat_id,
      utm_id: touch.utm_id,
    };
    for (const [key, value] of Object.entries(map)) {
      if (typeof value === "string" && value && !target.searchParams.has(key)) {
        target.searchParams.set(key, value);
      }
    }
    return target.pathname + target.search + target.hash;
  } catch {
    return url;
  }
}

/** Envia passo do funil + identificação numa única requisição.
 *  Duas requisições paralelas competiam pela mesma linha e uma delas falhava
 *  por chave duplicada — era isso que perdia o UTM no cadastro. */
export function trackStepAndIdentify(
  step: GrowthStep,
  identity: { userId?: string | null; companyId?: string | null; email?: string | null; name?: string | null; phone?: string | null },
  metadata: Record<string, unknown> = {},
): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  const touch = readTouch();
  const nextUserId = identity.userId ?? null;
  const nextCompanyId = identity.companyId ?? touch.company_id ?? null;
  const nextEmail = identity.email ?? touch.lead_email ?? null;
  const nextName = identity.name ?? touch.lead_name ?? null;
  const nextPhone = identity.phone ?? touch.lead_phone ?? null;
  writeTouch({
    ...touch,
    user_id: nextUserId,
    company_id: nextCompanyId,
    lead_email: nextEmail,
    lead_name: nextName,
    lead_phone: nextPhone,
  });
  try {
    return post({
      visitor_id: getVisitorId(),
      session: {
        ...sessionPayload(),
        user_id: nextUserId,
        company_id: nextCompanyId,
        lead_email: nextEmail,
        lead_name: nextName,
        lead_phone: nextPhone,
      },
      events: [
        {
          step,
          occurred_at: new Date().toISOString(),
          page_url: window.location.pathname + window.location.search,
          metadata,
        },
      ],
    });
  } catch {
    return Promise.resolve();
  }
}

/** Vincula o visitante anônimo ao usuário/empresa (e ao lead) após o cadastro. */
export function identifyGrowthVisitor(
  userId?: string | null,
  companyId?: string | null,
  lead?: { email?: string | null; name?: string | null; phone?: string | null },
): Promise<void> {
  if (typeof window === "undefined" || (!userId && !companyId && !lead?.email)) return Promise.resolve();
  const touch = readTouch();
  const nextUserId = userId ?? touch.user_id ?? null;
  const nextCompanyId = companyId ?? touch.company_id ?? null;
  const nextEmail = lead?.email ?? touch.lead_email ?? null;
  const nextName = lead?.name ?? touch.lead_name ?? null;
  const nextPhone = lead?.phone ?? touch.lead_phone ?? null;
  writeTouch({
    ...touch,
    user_id: nextUserId,
    company_id: nextCompanyId,
    lead_email: nextEmail,
    lead_name: nextName,
    lead_phone: nextPhone,
  });
  try {
    return post({
      visitor_id: getVisitorId(),
      session: {
        ...sessionPayload(),
        user_id: nextUserId,
        company_id: nextCompanyId,
        lead_email: nextEmail,
        lead_name: nextName,
        lead_phone: nextPhone,
      },
      events: [],
    });
  } catch {
    return Promise.resolve();
  }
}

/** CTA de teste grátis acionado por navegação programática (botões sem <a>). */
export function trackTrialCtaClick(label?: string): Promise<void> {
  return trackGrowthStep("cta_trial_click", {
    label: label?.slice(0, 80),
    from: typeof window !== "undefined" ? window.location.pathname : null,
  });
}

/** Registra visita — deduplicada por sessão de navegação e por caminho. */
export function trackSiteVisit(): Promise<void> {
  try {
    const key = `fastblog_growth_visit:${window.location.pathname}`;
    if (sessionStorage.getItem(key)) return Promise.resolve();
    sessionStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
  return trackGrowthStep("site_visit");
}